import { chatWithGptOss, GPT_OSS_INSTRUCTION } from '@/services/gptOssService';

type GptOssMessage = { role: 'system' | 'user' | 'assistant'; content: string };

const buildMessages = (system: string, user: string): GptOssMessage[] => [
  { role: 'system', content: `${GPT_OSS_INSTRUCTION}\n\n${system}` },
  { role: 'user', content: user }
];

export const gptOssCalculator = async (expression: string): Promise<string> => {
  const system = 'You are a precise calculator. Evaluate mathematical expressions exactly. Return ONLY the numeric result. No text.';
  const user = `Expression: ${expression}`;
  return await chatWithGptOss(buildMessages(system, user));
};

export const gptOssCalculatorWithExplanation = async (expression: string): Promise<{ result: string; explanation: string }> => {
  const result = (await gptOssCalculator(expression)).trim();
  const systemExplain = 'Explain briefly how the calculation is done. Be concise (1-3 short sentences).';
  const userExplain = `Explain the steps to compute: ${expression}`;
  const explanation = (await chatWithGptOss(buildMessages(systemExplain, userExplain))).trim();
  return { result, explanation };
};

export const gptOssTranslator = async (text: string, targetLang?: string): Promise<string> => {
  const system = targetLang
    ? `You are a professional translator. Translate the given text to ${targetLang}. Provide only the translation without any additional text or explanations.`
    : `You are a professional translator. Translate the given text to the most appropriate language based on context. If the text appears to be in English, translate to a commonly used language. If it's in another language, translate to English. Provide only the translation without any additional text or explanations.`;

  const user = targetLang ? `Translate to ${targetLang}: ${text}` : `Translate this text: ${text}`;
  const out = await chatWithGptOss(buildMessages(system, user));
  return out.trim().replace(/^\s*["']|["']\s*$/g, '');
};

export const gptOssTranslatorWithExplanation = async (text: string, targetLang?: string): Promise<{ translated: string; explanation: string }> => {
  const translated = await gptOssTranslator(text, targetLang);
  const systemExplain = 'Explain the key translation choices briefly (tone, idioms, notable word choices). Keep it concise (1-3 short sentences).';
  const userExplain = targetLang ? `Explain the translation choices for translating to ${targetLang}: ${text}` : `Explain the translation choices for: ${text}`;
  const explanation = (await chatWithGptOss(buildMessages(systemExplain, userExplain))).trim();
  return { translated, explanation };
};

export const gptOssCodeExplain = async (code: string): Promise<string> => {
  const system = 'You explain code clearly. Provide: What it does, key components, complexity, pitfalls, and improvements. Use concise sections and lists. Do not over explain when the explanation is short.';
  const user = `${code}`;
  return await chatWithGptOss(buildMessages(system, user));
};

export const gptOssUnitConvert = async (input: string): Promise<string> => {
  const system = 'You are a unit conversion assistant. Detect value, source unit, and target unit. Return a short one-line result like "10 km = 6.2137 miles". If unsupported, say "Unsupported conversion".';
  const user = `Convert: ${input}`;
  return await chatWithGptOss(buildMessages(system, user));
};


